import React from 'react';
import {InfoCircleOutlined} from '@ant-design/icons';
import {Col, Form, Input, InputNumber, Row, Select, Tooltip} from 'antd';
import _ from 'lodash';
import {Car} from '../../types/carTypes';

import {
    vehicleNumberRuleMessage,
    carDetailsRateClassToolTip,
    carDetailsNickNameToolTip,
    carDetailsVinToolTip,
} from '../../helpers/text';

import {formItemFullWidth} from '../../assets/styles';

interface CarDetailsFormProps {
    onSubmit: (car: Partial<Car>) => void;
    availableRateClasses: string[];
    car: Partial<Car>;
}

const CarDetailsForm = ({onSubmit, availableRateClasses = [], car}: CarDetailsFormProps) => {
    const [form] = Form.useForm();

    React.useEffect(() => {
        if (car) {
            form.setFieldsValue(car);
        }
    }, [form, car]);

    const formatInput = (e: React.FocusEvent<HTMLInputElement>, capitalize: boolean = true) => {
        let formattedValue = e.target.value.trim();
        if (capitalize) {
            formattedValue = formattedValue.toUpperCase();
        }

        form.setFieldValue(e.target.name, formattedValue);
    }

    return (
        <Form
            id="car-details"
            layout="vertical"
            form={form}
            onFinish={onSubmit}
        >
            <Row gutter={16}>
                <Col xs={24}>
                    <Form.Item
                        name="number"
                        label="Vehicle Number"
                        rules={[
                            {
                                required: true,
                                pattern: /^[\d]{0,9}$/, // Checks if length exceeds 9 and is at least 0.
                                message: vehicleNumberRuleMessage,
                            },
                        ]}
                    >
                        <InputNumber
                            style={{...formItemFullWidth}}
                            type="number"
                            name="number"
                            autoFocus={true}
                        />
                    </Form.Item>
                </Col>
                <Col xs={24}>
                    <Form.Item
                        name="licence_plate"
                        label="Licence Plate"
                        rules={[{type: 'string'}]}
                    >
                        <Input
                            name="licence_plate"
                            maxLength={15}
                            onBlur={(e) => formatInput(e)}
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Form.Item
                name="rate_class"
                label={
                    <span>Rate Class
                        <Tooltip title={carDetailsRateClassToolTip}>
                            <InfoCircleOutlined className="title-icon"/>
                        </Tooltip>
                    </span>
                }
            >
                <Select>
                    {
                        availableRateClasses && availableRateClasses.map((availableRateClass) => (
                            <Select.Option
                                key={availableRateClass}
                                value={availableRateClass}
                                name="rate_class"
                            >
                                <span> {_.startCase(availableRateClass)}</span>
                            </Select.Option>
                        ))
                    }
                </Select>
            </Form.Item>
            <Form.Item
                name="friendly_name"
                label={
                    <span>Nickname
                        <Tooltip title={carDetailsNickNameToolTip}>
                            <InfoCircleOutlined className="title-icon"/>
                        </Tooltip>
                    </span>
                }
                rules={[{type: 'string'}]}
            >
                <Input
                    name="friendly_name"
                    onBlur={(e) => formatInput(e, false)}
                />
            </Form.Item>
            <Form.Item
                name="vin"
                label={
                    <span>VIN
                        <Tooltip title={carDetailsVinToolTip}>
                            <InfoCircleOutlined className="title-icon"/>
                        </Tooltip>
                    </span>
                }
                rules={[{type: 'string'}]}
                required={!!car.vin} // Required when the value was set previously.
            >
                <Input
                    name="vin"
                    maxLength={17}
                    onBlur={(e) => formatInput(e)}
                />
            </Form.Item>
        </Form>
    );
}

export default CarDetailsForm;
